import { useMemo, memo } from 'react'

import { HcStructureMenuItem } from '@hotmart-org-ca/cosmos-web/dist/react/hc-structure'
import Icon from '@space-lib/components/icon'
import { CustomColorsUtils } from '@space-lib/utils/colors'
import { SessionMode } from '@space-shell/common/components/baseStructure/enum'
import { useParentInfoStore } from '@space-shell/remotes/preview/store/parentInfo'

import clsx from 'clsx'
import styled, { css } from 'styled-components'

import { TWENTY_PERCENT_ALPHA_HEX, HEX_LENGTH } from './constants'
import type { MenuItemProps } from './types'

export const HcMenuItem = styled(HcStructureMenuItem)<{
  $color: string
  $session?: SessionMode
  $isFixedColor?: boolean
}>`
  ${({ $color, $session, $isFixedColor }) => css`
    --hc-structure-menu-item-hover-background-color: ${$session === SessionMode.DEFAULT
      ? 'var(--custom-color-lighter)'
      : $isFixedColor
      ? 'var(--custom-color-lighter)'
      : `${$color.slice(0, HEX_LENGTH)}${TWENTY_PERCENT_ALPHA_HEX}`};

    --hc-structure-menu-item-active-background-color: ${$session === SessionMode.DEFAULT
      ? 'var(--custom-color-lightest)'
      : $isFixedColor
      ? 'var(--custom-color-lightest)'
      : $color};

    --hc-structure-menu-item-host-focus-icon-color: ${$isFixedColor
      ? 'var(--gray-900)'
      : CustomColorsUtils.getBestContrastColor($color)};

    --hc-structure-menu-item-host-focus-background-color: transparent;

    &:hover {
      span,
      svg {
        color: var(--gray-900) !important;
      }
    }
  `}
`
const Span = styled.span<{
  $color: string
  $isFixedColor: boolean
  $isActive?: boolean
}>`
  ${({ $isFixedColor, $isActive, $color }) =>
    `
    color: ${
      $isFixedColor
        ? 'var(--gray-900)'
        : $isActive
        ? CustomColorsUtils.getBestContrastColor($color)
        : 'var(--gray-900)'
    };
    `}
`

const CustomIcon = styled(Icon)<{ $color: string; $isFixedColor: boolean; $isActive?: boolean }>`
  ${({ $isFixedColor, $isActive, $color }) =>
    `
    color: ${
      $isFixedColor
        ? 'var(--gray-900)'
        : $isActive
        ? CustomColorsUtils.getBestContrastColor($color)
        : 'var(--gray-900)'
    };
`}
`

const MenuItem = ({
  label,
  session,
  color,
  iconPrimary,
  iconSecondary,
  onClick,
  isActive,
  className,
  classNameSpan = ''
}: MenuItemProps) => {
  const { color: previewColor } = useParentInfoStore()

  const spanClasses = clsx('_text-2', classNameSpan, {
    '_ml-0 _mr-2 _flex-1': Boolean(iconSecondary)
  })

  const isSessionPreviewEnable = session === SessionMode.PREVIEW

  const isMenuActive = useMemo(() => (isActive ? { active: true } : null), [isActive])

  return (
    <HcMenuItem
      slot='items'
      class={clsx(className)}
      $isFixedColor={Boolean(CustomColorsUtils.isFixedColor(color))}
      onClickItem={onClick}
      {...{ $session: isSessionPreviewEnable ? SessionMode.PREVIEW : SessionMode.DEFAULT }}
      {...{ $color: isSessionPreviewEnable ? previewColor : color }}
      {...isMenuActive}>
      {iconPrimary && (
        <div slot='icon'>
          <CustomIcon
            icon={iconPrimary}
            data-test='menu-icon-primary'
            $color={String(color)}
            $isFixedColor={Boolean(CustomColorsUtils.isFixedColor(color))}
            $isActive={Boolean(isMenuActive)}
          />
        </div>
      )}

      <Span
        className={spanClasses}
        $isFixedColor={CustomColorsUtils.isFixedColor(color)}
        $color={String(color)}
        $isActive={Boolean(isMenuActive)}>
        {label}
      </Span>

      {iconSecondary && (
        <div slot='icon'>
          <CustomIcon
            icon={iconSecondary}
            data-test='menu-icon-secondary'
            $color={String(color)}
            $isFixedColor={Boolean(CustomColorsUtils.isFixedColor(color))}
            $isActive={Boolean(isMenuActive)}
          />
        </div>
      )}
    </HcMenuItem>
  )
}

export default memo(MenuItem)
